// src/styles/theme.ts
import React from 'react';
import { createTheme } from '@mui/material/styles';
import { colors, gradients, shadows } from './colors';
export type UserType = 'traveller' | 'sender' | '' | 'generic';

// All prices are USD, returned directly by the API
export const CURRENCY_CODE = 'USD';
export const CURRENCY_SYMBOL = '$';

export interface Theme {
  colors: {
    primary: string;
    secondary: string;
    accent: string;
    background: string;
    text: string;
    textSecondary: string;
    gradient: string;
  };
  typography: {
    sectionHeading: React.CSSProperties;
    sectionSubheading: React.CSSProperties;
    bodyText: React.CSSProperties;
  };

  components: {
    section: {
      padding: {
        xs: string;
        sm: string;
        md: string;
        lg: string;
        xl: string;
        xxl: string;
      };
      container: {
        padding: {
          xs: number;
          sm: number;
          md: number;
          lg: number;
          xl: number;
          xxl: number;
        };
      };
    };
  };
}

// User-specific themes using centralized colors
const travellerTheme: Theme = {
  colors: {
    primary: colors.traveller.primary,
    secondary: colors.traveller.secondary,
    accent: colors.traveller.accent,
    background: colors.neutral.white,
    text: colors.traveller.text,
    textSecondary: colors.traveller.textSecondary,
    gradient: gradients.hero,
  },
  typography: {
    sectionHeading: {
      fontFamily: 'Poppins',
      fontWeight: 600,
      fontSize: 'clamp(28px, 4vw + 0.5rem, 48px)', // Increased max for xxl
      lineHeight: 1.2,
      textAlign: 'center',
    },
    sectionSubheading: {
      fontFamily: 'Poppins',
      fontWeight: 400,
      fontSize: 'clamp(14px, 0.5vw + 0.5rem, 18px)', // Increased max for xxl
      lineHeight: 1.5,
      textAlign: 'center',
    },
    bodyText: {
      fontFamily: 'Poppins',
      fontWeight: 400,
      fontSize: 'clamp(14px, 1vw + 0.5rem, 20px)', // Increased max for xxl
      lineHeight: 1.6,
    },
  },
  components: {
    section: {
      padding: {
        xs: '48px',
        sm: '64px',
        md: '80px',
        lg: '96px',
        xl: '112px',
        xxl: '128px',
      },
      container: {
        padding: {
          xs: 3,
          sm: 4,
          md: 5,
          lg: 6,
          xl: 7,
          xxl: 8,
        },
      },
    },
  },
};

const senderTheme: Theme = {
  colors: {
    primary: colors.sender.primary,
    secondary: colors.sender.secondary,
    accent: colors.sender.accent,
    background: colors.neutral.white,
    text: colors.sender.text,
    textSecondary: colors.sender.textSecondary,
    gradient: gradients.hero,
  },
  typography: {
    sectionHeading: {
      fontFamily: 'Poppins',
      fontWeight: 600,
      fontSize: 'clamp(28px, 4vw + 0.5rem, 48px)',
      lineHeight: 1.2,
      textAlign: 'center',
    },
    sectionSubheading: {
      fontFamily: 'Poppins',
      fontWeight: 400,
      fontSize: 'clamp(14px, 0.5vw + 0.5rem, 18px)',
      lineHeight: 1.5,
      textAlign: 'center',
    },
    bodyText: {
      fontFamily: 'Poppins',
      fontWeight: 400,
      fontSize: 'clamp(14px, 1vw + 0.5rem, 20px)',
      lineHeight: 1.6,
    },
  },
  components: {
    section: {
      padding: {
        xs: '48px',
        sm: '64px',
        md: '80px',
        lg: '96px',
        xl: '112px',
        xxl: '128px',
      },
      container: {
        padding: {
          xs: 3,
          sm: 4,
          md: 5,
          lg: 6,
          xl: 7,
          xxl: 8,
        },
      },
    },
  },
};

const genericTheme: Theme = {
  colors: {
    primary: colors.generic.primary,
    secondary: colors.generic.secondary,
    accent: colors.generic.accent,
    background: colors.neutral.white,
    text: colors.generic.text,
    textSecondary: colors.generic.textSecondary,
    gradient: gradients.hero,
  },
  typography: {
    sectionHeading: {
      fontFamily: 'Poppins',
      fontWeight: 600,
      fontSize: 'clamp(28px, 4vw + 0.5rem, 48px)',
      lineHeight: 1.2,
      textAlign: 'center',
    },
    sectionSubheading: {
      fontFamily: 'Poppins',
      fontWeight: 400,
      fontSize: 'clamp(14px, 0.5vw + 0.5rem, 18px)',
      lineHeight: 1.5,
      textAlign: 'center',
    },
    bodyText: {
      fontFamily: 'Poppins',
      fontWeight: 400,
      fontSize: 'clamp(14px, 1vw + 0.5rem, 20px)',
      lineHeight: 1.6,
    },
  },
  components: {
    section: {
      padding: {
        xs: '48px',
        sm: '64px',
        md: '80px',
        lg: '96px',
        xl: '112px',
        xxl: '128px',
      },
      container: {
        padding: {
          xs: 3,
          sm: 4,
          md: 5,
          lg: 6,
          xl: 7,
          xxl: 8,
        },
      },
    },
  },
};


export const getUserTheme = (userType: UserType): Theme => {
  switch (userType) {
    case 'traveller':
      return travellerTheme;
    case 'sender':
      return senderTheme;
    case 'generic':
      return genericTheme;
    default:
      return travellerTheme; // Default to traveller theme
  }
};

// Material UI theme configuration
/**
 * Creates a MUI theme dynamically based on user type
 */
export const createMuiTheme = (userType: UserType = 'traveller') => {
  const primaryColor = userType === 'sender' ? colors.sender.primary : userType === 'generic' ? colors.generic.primary : colors.traveller.primary;
  const secondaryColor = userType === 'sender' ? colors.sender.secondary : userType === 'generic' ? colors.generic.secondary : colors.traveller.secondary;

  return createTheme({
    breakpoints: {
      values: {
        xs: 0,
        sm: 640,
        md: 768,
        lg: 1024,
        xl: 1280,
        xxl: 1536,
      },
    },
    typography: {
      fontFamily: "'Poppins', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif",
      h1: {
        fontSize: 'clamp(2.5rem, 5vw + 1rem, 6rem)',
        fontWeight: 700,
        lineHeight: 1.2,
      },
      h2: {
        fontSize: 'clamp(2rem, 4vw + 0.5rem, 4.5rem)',
        fontWeight: 700,
        lineHeight: 1.3,
      },
      h3: {
        fontSize: 'clamp(1.5rem, 3vw + 0.5rem, 3.5rem)',
        fontWeight: 600,
        lineHeight: 1.4,
      },
      h4: {
        fontSize: 'clamp(1.25rem, 2vw + 0.5rem, 2.5rem)',
        fontWeight: 600,
        lineHeight: 1.4,
      },
      body1: {
        fontSize: 'clamp(0.875rem, 1vw + 0.5rem, 1.25rem)',
        fontWeight: 400,
        lineHeight: 1.5,
      },
      body2: {
        fontSize: 'clamp(0.875rem, 0.5vw + 0.5rem, 1.125rem)',
        fontWeight: 400,
        lineHeight: 1.6,
      },
    },
    spacing: 8, // Base spacing unit
    palette: {
      primary: {
        main: primaryColor,
      },
      secondary: {
        main: secondaryColor,
      },
      text: {
        primary: colors.form.label,
        secondary: colors.form.input,
      },
      background: {
        default: colors.neutral.white,
        paper: colors.neutral.white,
      },
      error: {
        main: colors.semantic.error,
      },
      warning: {
        main: colors.semantic.warning,
      },
      info: {
        main: colors.semantic.info,
      },
      success: {
        main: colors.semantic.success,
      },
      alert: {
        main: colors.semantic.alert,
      },
    },
  });
};

// Default export for backwards compatibility (traveller theme)
export const muiTheme = createMuiTheme('traveller');

export const responsiveSpacing = {
  containerPadding: {
    xs: '1.5rem',
    sm: '2rem',
    md: '2.5rem',
    lg: '3rem',
    xl: '3.5rem',
    xxl: '4rem',
    fluid: 'clamp(1.5rem, 2.5vw, 4rem)', 
  },
  sectionPadding: {
    xs: '3rem',
    sm: '4rem',
    md: '5rem',
    lg: '6rem',
    xl: '7rem',
    xxl: '8rem',
    fluid: 'clamp(3rem, 5vw, 8rem)', 
  },
  cardPadding: {
    xs: '1rem',
    sm: '1.25rem',
    md: '1.5rem',
    lg: '2rem',
    xl: '2.25rem',
    xxl: '2.5rem',
    fluid: 'clamp(1rem, 2vw, 2.5rem)', 
  },
  
  smallGap: {
    xs: '0.75rem',
    sm: '1rem',
    md: '1.25rem',
    lg: '1.5rem',
    xl: '1.75rem',
    xxl: '2rem',
    fluid: 'clamp(0.75rem, 1.5vw, 2rem)',
  },
  mediumGap: {
    xs: '1rem',
    sm: '1.5rem',
    md: '2rem',
    lg: '2.5rem',
    xl: '3rem',
    xxl: '3.5rem',
    fluid: 'clamp(1rem, 2vw, 3.5rem)',
  },
  largeGap: {
    xs: '2rem',
    sm: '2.5rem',
    md: '3rem',
    lg: '3.5rem',
    xl: '4rem',
    xxl: '4.5rem',
    fluid: 'clamp(2rem, 3vw, 4.5rem)',
  },
};

const containerSizes = {
  sm: 640,
  md: 768,
  lg: 1024,
  xl: 1280,
  xxl: 1536,
  header: 1360,
  content: 924,
  form: 640,
  page: 1440,
  wide: 1600,
  ultraWide: 1920,
} as const;

export const containerMaxWidths = {
  ...containerSizes,
  withPadding: (breakpoint: keyof typeof containerSizes, padding: number = 40) => {
    const width = containerSizes[breakpoint];
    return width + (padding * 2);
  },
};

export const viewportUtils = {
  maxContentWidth: '100vw',
  safeMaxWidth: 'min(100vw, 1920px)', 
  centeredMaxWidth: 'min(100%, 1440px)', 
  
  responsiveContainer: `
    width: 100%;
    max-width: min(100vw, 1920px);
    margin-left: auto;
    margin-right: auto;
    padding-left: clamp(1.5rem, 2.5vw, 4rem);
    padding-right: clamp(1.5rem, 2.5vw, 4rem);
  `,
};

export const FONT_FAMILY = 'Poppins';

export const SEARCH_DEFAULTS = {
  RADIUS: 500,
  MIN_WEIGHT: 0,
  MAX_WEIGHT: 100,
  WEIGHT_UNIT: 'lb' as const,
  SORT_BY: 'latest' as const,
} as const;

/**
 * Standard date/time format options used across the entire app.
 * Display format: "Nov 19, 2026"  (month abbr, numeric day, numeric year)
 * Time format:    "2:45 PM"       (12-hour with AM/PM)
 * Combined:       "Nov 19, 2026, 2:45 PM"
 *
 * Always use 'en-US' locale so the output is consistent regardless of
 * the user's browser language.  Timezone-awareness is preserved via
 * the timeZone option that callers may pass through Intl.DateTimeFormatOptions.
 */
export const DATE_FORMAT_LOCALE = 'en-US' as const;

export const DATE_FORMAT_OPTIONS: Intl.DateTimeFormatOptions = {
  year: 'numeric',
  month: 'short',
  day: 'numeric',
} as const;

export const DATE_TIME_FORMAT_OPTIONS: Intl.DateTimeFormatOptions = {
  year: 'numeric',
  month: 'short',
  day: 'numeric',
  hour: 'numeric',
  minute: '2-digit',
  hour12: true,
} as const;

export const TIME_FORMAT_OPTIONS: Intl.DateTimeFormatOptions = {
  hour: 'numeric',
  minute: '2-digit',
  hour12: true,
} as const;

export { colors, gradients, shadows } from './colors';

declare module '@mui/material/styles' {
  interface BreakpointOverrides {
    xs: true;
    sm: true;
    md: true;
    lg: true;
    xl: true;
    xxl: true;
  }

  interface Palette {
    alert: Palette['primary'];
  }

  interface PaletteOptions {
    alert?: PaletteOptions['primary'];
  }
}

declare module '@mui/material/Button' {
  interface ButtonPropsColorOverrides {
    alert: true;
  }
}

export default muiTheme;

export interface MainScreenLayout {
  container: {
    maxWidth: string;
    padding: ResponsiveSpacing;
    gap: string;
  };
  form: {
    background: string;
    border: string;
    borderRadius: string;
    boxShadow: string;
  };
  section: {
    padding: ResponsiveSpacing;
    gap: string;
  };
  input: {
    height: string;
    borderRadius: string;
    fontSize: string;
    border: string;
    focusBorder: string;
  };
  progressStepper: {
    stepCircle: {
      size: string;
      activeBorder: string;
    };
    line: {
      height: string;
      color: string;
      activeColor: string;
    };
  };
}

interface ResponsiveSpacing {
  xs: string;
  sm: string;
  md: string;
  lg: string;
  xl: string;
  xxl: string;
}

// Add to getUserTheme return type
export const getSenderMainScreenLayout = (): MainScreenLayout => ({
  container: {
    maxWidth: '1200px',
    padding: {
      xs: '24px 16px',
      sm: '32px 24px',
      md: '40px 40px',
      lg: '40px 80px',
      xl: '40px 80px',
      xxl: '40px 80px',
    },
    gap: '48px',
  },
  form: {
    background: colors.neutral.white,
    border: `1px solid ${colors.neutral.gray300}`,
    borderRadius: '12px',
    boxShadow: shadows.xs,
  },
  section: {
    padding: {
      xs: '24px 16px',
      sm: '32px 24px',
      md: '40px 40px',
      lg: '40px 80px',
      xl: '40px 80px',
      xxl: '40px 80px',
    },
    gap: '48px',
  },
  input: {
    height: '44px',
    borderRadius: '8px',
    fontSize: '16px',
    border: `1px solid ${colors.neutral.gray400}`,
    focusBorder: `2px solid ${colors.sender.primary}`,
  },
  progressStepper: {
    stepCircle: {
      size: '40px',
      activeBorder: `0px 0px 0px 2px ${colors.neutral.white}, 0px 0px 0px 4px ${colors.sender.primary}`,
    },
    line: {
      height: '2px',
      color: colors.neutral.gray200,
      activeColor: colors.sender.primary,
    },
  },
});